import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { theme } from '../../../theme';
import { Card, CardHeader, CardTitle, CardBody } from '../../ui/Card';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useAuth } from '../../../contexts/AuthContext';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { StaffData } from '../../../firebase/auth';

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing[6]};
  margin-bottom: ${theme.spacing[6]};

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled(Card)`
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03) !important;
  border: 1px solid ${theme.colors.light.border.light};
  
  &:hover {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
  }
`;

const FullWidthChartCard = styled(ChartCard)`
  grid-column: 1 / -1;
`;

const ChartContainer = styled.div`
  width: 100%;
  height: 400px;
  padding: ${theme.spacing[2]};
`;

const COLORS = [
  theme.colors.primary[500],
  theme.colors.primary[600],
  theme.colors.primary[400],
  theme.colors.primary[700],
  theme.colors.primary[300],
];

const StaffAnalyticsCharts: React.FC = () => {
  const { staffData, companyData } = useAuth();
  const [staffList, setStaffList] = useState<StaffData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStaffData = async () => {
      if (!companyData?.id) {
        setLoading(false);
        return;
      }

      try {
        const staffRef = collection(db, 'companies', companyData.id, 'staff');
        const querySnapshot = await getDocs(staffRef);
        const staff: StaffData[] = [];
        querySnapshot.forEach((doc) => {
          staff.push(doc.data() as StaffData);
        });
        setStaffList(staff);
      } catch (error) {
        console.error('Error fetching staff:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStaffData();
  }, [companyData]);

  // Filter staff by department if staff member has a department
  const currentDepartment = staffData?.department || staffData?.departments?.[0];
  const departmentStaff = currentDepartment
    ? staffList.filter((s) => s.department === currentDepartment || s.departments?.includes(currentDepartment))
    : staffList;

  // Prepare data for department distribution chart
  const departmentData = departmentStaff.reduce((acc, staff) => {
    const dept = staff.department || 'Unassigned';
    acc[dept] = (acc[dept] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const departmentChartData = Object.entries(departmentData).map(([name, value]) => ({
    name,
    value,
  }));

  // Prepare data for role distribution pie chart
  const roleData = departmentStaff.reduce((acc, staff) => {
    const role = staff.role || 'staff';
    acc[role] = (acc[role] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const roleChartData = Object.entries(roleData).map(([name, value]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1),
    value,
  }));

  // Prepare data for monthly staff growth (last 6 months)
  const monthlyData = Array.from({ length: 6 }, (_, i) => {
    const date = new Date();
    date.setMonth(date.getMonth() - (5 - i));
    const monthName = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();
    
    const count = departmentStaff.filter((s) => {
      if (!s.createdAt) return false;
      // Handle both Date and Firestore Timestamp
      let createdDate: Date;
      if (s.createdAt instanceof Date) {
        createdDate = s.createdAt;
      } else if (s.createdAt && typeof s.createdAt === 'object' && 'toDate' in s.createdAt) {
        createdDate = (s.createdAt as any).toDate();
      } else {
        return false;
      }
      return (
        createdDate.getMonth() === date.getMonth() &&
        createdDate.getFullYear() === date.getFullYear() &&
        createdDate <= date
      );
    }).length;
    
    return { month: `${monthName} ${year}`, staff: count };
  });

  if (loading) {
    return (
      <ChartsGrid>
        <ChartCard>
          <CardBody>
            <div style={{ textAlign: 'center', padding: theme.spacing[8] }}>
              Loading charts...
            </div>
          </CardBody>
        </ChartCard>
      </ChartsGrid>
    );
  }

  return (
    <ChartsGrid>
      {/* Department Distribution Bar Chart */}
      <ChartCard>
        <CardHeader>
          <CardTitle>Staff by Department</CardTitle>
        </CardHeader>
        <CardBody>
          <ChartContainer>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={departmentChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.light.border.light} />
                <XAxis
                  dataKey="name"
                  stroke={theme.colors.light.text.secondary}
                  fontSize={12}
                  tick={{ fill: theme.colors.light.text.secondary }}
                />
                <YAxis
                  stroke={theme.colors.light.text.secondary}
                  tick={{ fill: theme.colors.light.text.secondary }}
                  fontSize={12}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: theme.colors.white,
                    border: `1px solid ${theme.colors.light.border.medium}`,
                    borderRadius: theme.borderRadius.md,
                    boxShadow: theme.shadows.lg,
                    color: theme.colors.light.text.primary,
                  }}
                  labelStyle={{ color: theme.colors.light.text.primary }}
                />
                <Bar dataKey="value" fill={theme.colors.primary[500]} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        </CardBody>
      </ChartCard>

      {/* Role Distribution Pie Chart */}
      <ChartCard>
        <CardHeader>
          <CardTitle>Staff by Role</CardTitle>
        </CardHeader>
        <CardBody>
          <ChartContainer>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={roleChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => percent ? `${name}: ${(percent * 100).toFixed(0)}%` : name}
                  outerRadius={120}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {roleChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: theme.colors.white,
                    border: `1px solid ${theme.colors.light.border.medium}`,
                    borderRadius: theme.borderRadius.md,
                    boxShadow: theme.shadows.lg,
                    color: theme.colors.light.text.primary,
                  }}
                />
                <Legend
                  wrapperStyle={{ paddingTop: theme.spacing[4] }}
                  iconType="circle"
                />
              </PieChart>
            </ResponsiveContainer>
          </ChartContainer>
        </CardBody>
      </ChartCard>

      {/* Monthly Growth Chart */}
      <FullWidthChartCard>
        <CardHeader>
          <CardTitle>Staff Growth (Last 6 Months)</CardTitle>
        </CardHeader>
        <CardBody>
          <ChartContainer>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.light.border.light} />
                <XAxis
                  dataKey="month"
                  stroke={theme.colors.light.text.secondary}
                  fontSize={12}
                  tick={{ fill: theme.colors.light.text.secondary }}
                />
                <YAxis
                  stroke={theme.colors.light.text.secondary}
                  tick={{ fill: theme.colors.light.text.secondary }}
                  fontSize={12}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: theme.colors.white,
                    border: `1px solid ${theme.colors.light.border.medium}`,
                    borderRadius: theme.borderRadius.md,
                    boxShadow: theme.shadows.lg,
                    color: theme.colors.light.text.primary,
                  }}
                  labelStyle={{ color: theme.colors.light.text.primary }}
                />
                <Bar dataKey="staff" fill={theme.colors.primary[600]} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        </CardBody>
      </FullWidthChartCard>
    </ChartsGrid>
  );
};

export default StaffAnalyticsCharts;

